import * as B from '@blueprintjs/core';
import React from 'react';

import {
  SnapshotThumbnail,
  formatAcresLabel,
  formatOrderedItemPrice,
  formatSensingDate,
  isPartialAoiItem,
  resolutionLabel,
} from 'app/modules/Cart';

import type {OrderedLine} from '../types';
import cs from './styles.module.scss';

export interface OrderedSceneRowProps {
  line: OrderedLine;
  /** The property card already names the property, so only the portfolio view repeats it. */
  showProperty: boolean;
}

/**
 * One scene on the confirmation, named the way a person identifies it: property, source, and when
 * it was sensed. That trio is also the tuple the checkout endpoint keys a purchase on, so support
 * can find the scene from what the row already says.
 */
export const OrderedSceneRow: React.FC<OrderedSceneRowProps> = ({line, showProperty}) => {
  const {ordered, item} = line;
  const featureName = item?.snapshot.featureName ?? `Feature ${ordered.featureId}`;
  const heading = [
    item?.snapshot.sourceDisplayName ?? ordered.sourceId,
    resolutionLabel(ordered.sourceId),
  ]
    .filter(Boolean)
    .join(' · ');
  const meta = [
    showProperty ? featureName : null,
    `Sensed ${formatSensingDate(ordered.sensingTime, ordered.sourceId)}`,
    formatAcresLabel(ordered.billedAcres),
  ].filter(Boolean);

  return (
    <div className={cs.row}>
      <div className={cs.thumbnail}>
        <SnapshotThumbnail
          url={item?.snapshot.thumbnailUrl ?? null}
          isOverride={item?.snapshot.thumbnailIsOverride}
          alt={`${featureName} preview`}
        />
      </div>

      <div className={cs.details}>
        <div className={cs.headingLine}>
          <span className={cs.heading}>{heading}</span>
          {/* An unpaired line's sub-area is unknown rather than absent, so it carries no tag. */}
          {item && isPartialAoiItem(item) && (
            <B.Tag minimal round className={cs.partialTag}>
              Partial area
            </B.Tag>
          )}
        </div>
        <div className={cs.meta}>{meta.join(' · ')}</div>
      </div>

      <div className={cs.price}>{formatOrderedItemPrice(ordered)}</div>
    </div>
  );
};

export default OrderedSceneRow;
